import { useEffect, useState } from "react";
import { useThemeColor, hexAlpha } from "@/hooks/useThemeColor";
import { useDesignState } from "@/hooks/useDesignSystem";
import { HERO_IMAGE_PRESETS } from "@/pages/admin/components/design/designConstants";

const taglines = ["بلايستيشن 5", "بلايستيشن 4", "كمبيوتر ألعاب", "إكسسوارات"];

export default function Hero() {
  const [tagIndex, setTagIndex] = useState(0);
  const tc = useThemeColor();
  const { identity } = useDesignState();

  const heroPreset = HERO_IMAGE_PRESETS.find(h => h.id === identity.heroImageId) ?? HERO_IMAGE_PRESETS[0];
  const heroImageUrl = identity.useCustomHeroImage && identity.heroImageUrl
    ? identity.heroImageUrl
    : heroPreset.url;
  const overlayOpacity = (identity.heroOverlayOpacity ?? 40) / 100;
  const heroTitle = identity.heroTitle || 'أفضل أجهزة الألعاب والإكسسوارات';
  const heroSubtitle = identity.heroSubtitle || 'وجهتك الأولى لعالم الألعاب — أجهزة، أيادي تحكم، وإكسسوارات بأفضل الأسعار';
  const heroCta = identity.heroCta || 'تسوق الآن';

  useEffect(() => {
    const t = setInterval(() => {
      setTagIndex((prev) => (prev + 1) % taglines.length);
    }, 2000);
    return () => clearInterval(t);
  }, []);

  const scrollTo = (id: string) => {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={heroImageUrl}
          alt="Gaming Background"
          className="w-full h-full object-cover object-top"
        />
        <div
          className="absolute inset-0"
          style={{ background: `rgba(0,0,0,${overlayOpacity})` }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#0a0a0a]"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30"></div>
        {/* Ambient glow — uses theme color */}
        <div className="absolute inset-0" style={{
          background: `radial-gradient(ellipse at center, ${hexAlpha(tc.primary, 0.08)} 0%, transparent 70%)`
        }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full text-center px-4 pt-24 pb-16 flex flex-col items-center">
        {/* Logo Mark */}
        <div className="mb-8 animate-[fadeInDown_0.8s_ease_both]">
          <div className="relative inline-flex items-center justify-center">
            <div className="absolute w-24 h-24 md:w-32 md:h-32 rounded-full border animate-[ebdaa-rotate_12s_linear_infinite]"
              style={{ borderColor: hexAlpha(tc.primary, 0.2) }}></div>
            <div className="absolute w-28 h-28 md:w-36 md:h-36 rounded-full border animate-[ebdaa-rotate_20s_linear_infinite_reverse]"
              style={{ borderColor: hexAlpha(tc.primary, 0.1) }}></div>
            <div className="relative w-20 h-20 md:w-28 md:h-28 flex items-center justify-center">
              <div className="absolute inset-0 rounded-2xl border"
                style={{
                  background: `linear-gradient(135deg, ${hexAlpha(tc.primary, 0.2)} 0%, ${hexAlpha(tc.primary, 0.05)} 100%)`,
                  borderColor: hexAlpha(tc.primary, 0.4),
                }}></div>
              <div className="absolute inset-0 rounded-2xl"
                style={{ boxShadow: `0 0 40px ${hexAlpha(tc.primary, 0.2)}` }}></div>
              <span className="relative text-5xl md:text-6xl font-black"
                style={{
                  fontFamily: "'Amiri', 'Tajawal', serif",
                  color: tc.primary,
                  textShadow: `0 0 30px ${hexAlpha(tc.primary, 0.8)}, 0 0 60px ${hexAlpha(tc.primary, 0.4)}`,
                }}>
                إ
              </span>
            </div>
          </div>
        </div>

        {/* Main Title */}
        <div className="animate-[fadeInDown_0.9s_ease_both]">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-3 tracking-wide max-w-3xl"
            style={{
              fontFamily: "'Amiri', 'Tajawal', serif",
              background: "linear-gradient(to bottom, #ffffff 0%, #f5f5f5 50%, #e5e5e5 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter: `drop-shadow(0 0 40px ${hexAlpha(tc.primary, 0.3)}) drop-shadow(0 4px 20px rgba(0,0,0,0.8))`,
            }}>
            {heroTitle}
          </h1>
          <p className="text-sm md:text-base font-medium tracking-[0.4em] uppercase mb-8"
            style={{ fontFamily: "'Tajawal', sans-serif", color: hexAlpha(tc.primary, 0.9) }}>
            AL EBDAA GAME STORE
          </p>
        </div>

        {/* Animated Tagline */}
        <div className="h-14 mb-6 animate-[fadeIn_1s_ease_both]">
          <p key={tagIndex} className="text-2xl md:text-4xl font-bold animate-[fadeIn_0.5s_ease_both]"
            style={{
              fontFamily: "'Amiri', 'Tajawal', serif",
              background: `linear-gradient(90deg, ${tc.primaryLight} 0%, ${tc.primary} 25%, ${tc.primaryDark} 50%, ${tc.primary} 75%, ${tc.primaryLight} 100%)`,
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              animation: "ebdaa-shimmer 3s linear infinite",
              textShadow: `0 0 20px ${hexAlpha(tc.primary, 0.3)}`,
            }}>
            {taglines[tagIndex]}
          </p>
        </div>

        <p className="text-gray-400/90 text-base md:text-lg max-w-xl mb-12 leading-relaxed animate-[fadeIn_1.1s_ease_both]"
          style={{ fontFamily: "'Tajawal', sans-serif", fontWeight: 300 }}>
          {heroSubtitle}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center animate-[fadeInUp_1.2s_ease_both]">
          <button
            onClick={() => scrollTo("#products")}
            className="group relative font-bold px-10 py-4 rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer overflow-hidden"
            style={{
              background: `linear-gradient(to right, ${tc.primary}, ${tc.primaryDark})`,
              color: tc.textOnPrimary,
              fontFamily: "'Tajawal', sans-serif",
            }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = `linear-gradient(to right, ${tc.primaryLight}, ${tc.primary})`}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = `linear-gradient(to right, ${tc.primary}, ${tc.primaryDark})`}
          >
            <span className="relative z-10">{heroCta}</span>
            <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500"></div>
          </button>
          <button
            onClick={() => scrollTo("#devices")}
            className="group relative font-semibold px-10 py-4 rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer overflow-hidden border"
            style={{
              borderColor: hexAlpha(tc.primary, 0.5),
              color: tc.primary,
              fontFamily: "'Tajawal', sans-serif",
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.borderColor = tc.primary;
              (e.currentTarget as HTMLElement).style.color = tc.primaryLight;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.5);
              (e.currentTarget as HTMLElement).style.color = tc.primary;
            }}
          >
            <span className="relative z-10">استعرض الأجهزة</span>
            <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500"
              style={{ background: hexAlpha(tc.primary, 0.1) }}></div>
          </button>
        </div>

        {/* Stats */}
        <div className="mt-20 grid grid-cols-3 gap-8 md:gap-20 animate-[fadeInUp_1.4s_ease_both]">
          {[
            { num: "+500", label: "منتج" },
            { num: "+5000", label: "عميل" },
            { num: "5★", label: "تقييم" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-3xl md:text-4xl font-bold mb-1"
                style={{
                  fontFamily: "'Amiri', 'Tajawal', serif",
                  color: tc.primary,
                  textShadow: `0 0 20px ${hexAlpha(tc.primary, 0.4)}`,
                }}>
                {s.num}
              </p>
              <p className="text-gray-500 text-sm" style={{ fontFamily: "'Tajawal', sans-serif", fontWeight: 300 }}>
                {s.label}
              </p>
            </div>
          ))}
        </div>

        {/* Scroll indicator */}
        <button
          onClick={() => scrollTo("#products")}
          className="mt-20 flex flex-col items-center gap-2 text-gray-500 transition-colors duration-300 cursor-pointer group"
          onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = tc.primary}
          onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = ''}
        >
          <span className="text-xs tracking-[0.3em]" style={{ fontFamily: "'Tajawal', sans-serif" }}>اكتشف المزيد</span>
          <i className="ri-arrow-down-line text-xl animate-bounce"></i>
        </button>
      </div>

      <style>{`
        @keyframes ebdaa-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes ebdaa-shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>
    </section>
  );
}
